// ===========================================================================
// Account + billing client (adult-only monetization). Talks to /api/account and
// /api/billing on the same origin. The account session token lives in
// localStorage; the chat flow passes it to /api/session so Plus perks apply.
// ASCII only.
// ===========================================================================

const TOKEN_KEY = 'lumi.account.token';

export class AccountClient {
  constructor() {
    this.token = localStorage.getItem(TOKEN_KEY) || null;
    this.account = null;
    this.roomPro = [];
  }

  get loggedIn() {
    return !!this.token;
  }

  /** The signed-in account's username, or null. */
  get username() {
    return (this.account && this.account.username) || null;
  }

  get isPlus() {
    return !!(this.account && this.account.plus);
  }

  get settings() {
    return (this.account && this.account.settings) || {};
  }

  get cosmetics() {
    return (this.account && this.account.cosmetics) || [];
  }

  setToken(t) {
    this.token = t || null;
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
  }

  _headers(json) {
    const h = {};
    if (json) h['Content-Type'] = 'application/json';
    if (this.token) h.Authorization = `Bearer ${this.token}`;
    return h;
  }

  async _post(path, body) {
    const r = await fetch(path, { method: 'POST', headers: this._headers(true), body: JSON.stringify(body || {}) });
    return { status: r.status, data: await r.json().catch(() => ({})) };
  }

  async _get(path) {
    const r = await fetch(path, { headers: this._headers(false) });
    return { status: r.status, data: await r.json().catch(() => ({})) };
  }

  /** Load the current account (if a token is present). */
  async refresh() {
    if (!this.token) {
      this.account = null;
      return null;
    }
    const { status, data } = await this._get('/api/account/me');
    if (status === 200) {
      this.account = data.account;
      this.roomPro = data.roomPro || [];
      return this.account;
    }
    if (status === 401) {
      this.setToken(null);
      this.account = null;
    }
    return null;
  }

  /** Exchange a birth year for an age token (band proof) needed to register. */
  async ageToken(birthYear) {
    const r = await fetch('/api/session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ birthYear }),
    });
    const d = await r.json().catch(() => ({}));
    return { status: r.status, ageToken: d.ageToken, band: d.band, error: d.error };
  }

  async register(username, password, ageToken) {
    const { status, data } = await this._post('/api/account/register', { username, password, ageToken });
    if (status === 200) {
      this.setToken(data.token);
      this.account = data.account;
    }
    return { status, data };
  }

  async login(username, password) {
    const { status, data } = await this._post('/api/account/login', { username, password });
    if (status === 200) {
      this.setToken(data.token);
      this.account = data.account;
    }
    return { status, data };
  }

  /** First login for an admin-created (claimable) account: set the password. */
  async claim(username, password, ageToken) {
    const { status, data } = await this._post('/api/account/claim', { username, password, ageToken });
    if (status === 200) {
      this.setToken(data.token);
      this.account = data.account;
    }
    return { status, data };
  }

  /**
   * Set a new password using the code from signup.
   *
   * A 403 carries attemptsLeft, so the form can tell somebody how many tries
   * remain instead of letting them rattle the same door forever, and exhausted
   * once there is a person to ask instead.
   */
  async recover(username, code, password, fingerprint) {
    const { status, data } = await this._post('/api/account/recover', {
      username, code, password, fingerprint,
    });
    if (status === 200) {
      this.setToken(data.token);
      this.account = data.account;
    }
    return { status, data };
  }

  /** Lost the password AND the code: ask a human. Always answers ok. */
  async recoveryHelp(username, fingerprint) {
    const { status, data } = await this._post('/api/account/recovery-help', { username, fingerprint });
    return { status, data };
  }

  /** A one-shot notice for the client to celebrate (e.g. an admin-granted Plus), or null. */
  get notice() {
    return (this.account && this.account.notice) || null;
  }

  /** Clear the notice once its banner has been shown. */
  async ackNotice() {
    const { status, data } = await this._post('/api/account/notice', {});
    if (status === 200) this.account = data.account;
    return { status, data };
  }

  logout() {
    this.setToken(null);
    this.account = null;
    this.roomPro = [];
  }

  async billingMode() {
    const { data } = await this._get('/api/billing/mode');
    return data.mode;
  }

  async checkout(body) {
    return this._post('/api/billing/checkout', body);
  }

  async stubComplete(token) {
    const { status, data } = await this._post('/api/billing/stub-complete', { token });
    if (status === 200) {
      this.account = data.account;
      this.roomPro = data.roomPro || [];
    }
    return { status, data };
  }

  async saveSettings(patch) {
    const { status, data } = await this._post('/api/account/settings', patch);
    if (status === 200) this.account = data.account;
    return { status, data };
  }

  async roomSettings(code, patch) {
    const { status, data } = await this._post('/api/account/room-settings', { code, ...patch });
    if (status === 200) this.roomPro = (this.roomPro || []).map((r) => (data.room && r.code === data.room.code ? data.room : r));
    return { status, data };
  }

  /**
   * What may be paid with right now: the two switches, and the coins that
   * have both a price we trust and an address to give. Asked with the amount
   * so each coin can show what it would actually cost.
   */
  async payMethods(usdCents) {
    const { status, data } = await this._get(`/api/billing/methods?usdCents=${Number(usdCents) || 499}`);
    return status === 200 ? data : { card: false, crypto: false, coins: [] };
  }

  /** Open a crypto invoice. The price is the server's, never ours. */
  async cryptoInvoice(body) {
    const { status, data } = await this._post('/api/billing/crypto/invoice', body);
    return status === 200 ? { ok: true, invoice: data.invoice } : { ok: false, error: data.error || 'unavailable' };
  }

  /** What a front-page promotion would cost: the window and the price, no
   *  sign-in needed and nothing held. */
  async promoQuote(params) {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params || {})) if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    const { status, data } = await this._get(`/api/billing/promo/quote?${q}`);
    return status === 200 ? { ok: true, ...data } : { ok: false, error: data.error || 'unavailable' };
  }

  /** How is that invoice doing? Also returns the account, so a grant shows. */
  async cryptoStatus(id) {
    const { status, data } = await this._get(`/api/billing/crypto/invoice/${encodeURIComponent(id)}`);
    if (status !== 200) return { ok: false };
    if (data.account) this.account = data.account;
    return { ok: true, invoice: data.invoice, account: data.account };
  }

  /**
   * Run a purchase. In stub mode it completes in place (no redirect); with Stripe it
   * redirects to the hosted checkout. Returns { ok, mode } or { ok:false }.
   */
  async buy(body) {
    const { status, data } = await this.checkout(body);
    if (status !== 200 || data.error) return { ok: false, error: data.error || 'unavailable' };
    if (data.mode === 'stub') {
      const token = new URL(data.url, location.origin).searchParams.get('token');
      const res = await this.stubComplete(token);
      return { ok: res.status === 200, mode: 'stub' };
    }
    if (data.url) {
      window.location.href = data.url; // Stripe hosted checkout
      return { ok: true, mode: 'stripe', redirecting: true };
    }
    return { ok: false };
  }
}

export default AccountClient;
